import React, { useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Link,
  Breadcrumbs,
  Chip,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import changelogMd from '../../CHANGELOG.md?raw';
import { UI_VERSION } from '../version';
import { parseChangelog } from '../utils/changelogParser';

const ChangelogPage: React.FC = () => {
  const releases = useMemo(() => parseChangelog(changelogMd), []);

  return (
    <Box sx={{ backgroundColor: '#ffffff', minHeight: '60vh', py: { xs: 4, md: 6 } }}>
      <Container maxWidth="md">
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/" underline="hover" color="text.secondary">
            Home
          </Link>
          <Typography color="text.primary">Changelog</Typography>
        </Breadcrumbs>

        <Typography
          component="h1"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '2rem', md: '2.5rem' },
            mb: 1,
            letterSpacing: '-0.5px',
          }}
        >
          UI Changelog
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Current version: <strong>v{UI_VERSION}</strong>
        </Typography>

        {releases.length === 0 ? (
          <Typography color="text.secondary">No release notes are available yet.</Typography>
        ) : (
          releases.map((release, index) => (
            <Box
              key={release.version}
              component="article"
              sx={{
                mb: index < releases.length - 1 ? 5 : 0,
                pb: index < releases.length - 1 ? 4 : 0,
                borderBottom: index < releases.length - 1 ? '1px solid' : 'none',
                borderColor: 'grey.200',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'baseline',
                  gap: 1.5,
                  mb: release.released ? 0.5 : 1.5,
                }}
              >
                <Typography
                  component="h2"
                  sx={{
                    fontSize: { xs: '1.15rem', md: '1.35rem' },
                    fontWeight: 700,
                    letterSpacing: '-0.25px',
                  }}
                >
                  v{release.version}
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: 'inherit',
                      ml: 1,
                    }}
                  >
                    — {release.title}
                  </Typography>
                </Typography>
                {release.version === UI_VERSION && (
                  <Chip label="Current" size="small" color="primary" sx={{ fontWeight: 600 }} />
                )}
              </Box>

              {release.released && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Released {release.released}
                </Typography>
              )}

              {release.sections.map((section) => (
                <Box key={`${release.version}-${section.title}`} sx={{ mb: 2.5 }}>
                  <Typography
                    component="h3"
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                  >
                    {section.title}
                  </Typography>
                  {section.items.length > 0 ? (
                    <Box
                      component="ul"
                      sx={{
                        m: 0,
                        pl: 2.5,
                        '& li': {
                          lineHeight: 1.7,
                          color: 'text.secondary',
                          mb: 0.75,
                          pl: 0.5,
                        },
                      }}
                    >
                      {section.items.map((item) => (
                        <Box component="li" key={item}>
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              p: ({ children }) => <>{children}</>,
                              strong: ({ children }) => (
                                <Box component="strong" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                  {children}
                                </Box>
                              ),
                            }}
                          >
                            {item}
                          </ReactMarkdown>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No items listed.
                    </Typography>
                  )}
                </Box>
              ))}

              {index < releases.length - 1 && <Divider sx={{ mt: 1, display: { xs: 'none', md: 'block' } }} />}
            </Box>
          ))
        )}
      </Container>
    </Box>
  );
};

export default ChangelogPage;
